import { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Clock, AlertTriangle } from 'lucide-react';
import { formatWaitTime } from '@/utils/rateLimitUtils';

interface RateLimitCountdownProps {
  initialSeconds: number;
  type: 'ip' | 'email' | 'unknown';
  onExpire?: () => void;
}

export const RateLimitCountdown = ({ initialSeconds, type, onExpire }: RateLimitCountdownProps) => {
  const [remainingSeconds, setRemainingSeconds] = useState(initialSeconds);

  useEffect(() => {
    if (remainingSeconds <= 0) {
      onExpire?.();
      return;
    }

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingSeconds, onExpire]);

  if (remainingSeconds <= 0) return null;

  return (
    <Alert variant="destructive" className="mb-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle className="flex items-center gap-2">
        <Clock className="h-4 w-4" />
        Rate Limited - {formatWaitTime(remainingSeconds)} remaining
      </AlertTitle>
      <AlertDescription>
        {type === 'ip' ? (
          <>
            You've reached the server's request limit. This is based on your IP address, 
            so <strong>using a different email won't help</strong>. Please wait for the 
            countdown to complete before trying again.
          </>
        ) : (
          <>
            Too many attempts with this email address. You can either wait for the 
            countdown or try with a different email address.
          </>
        )}
      </AlertDescription>
    </Alert>
  );
};
